/**
 * VersionHistoryOffcanvas Component (V2)
 *
 * Tailwind Sheet mit Version History.
 * Sections: Live (Current), Active (Schedule), Upcoming, History.
 *
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { ContentBlockContent } from '@/types/content-blocks'
import { History, X, CheckCircle, Clock, AlertTriangle, RotateCcw, Loader2 } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Button,
  Alert,
  AlertDescription,
} from '@/components/v2/ui'
import { VersionCard, VersionSection } from './VersionCard'
import { DiffRouter } from './diff/DiffRouter'
import {
  useVersionHistory,
  useGroupedVersionHistory,
  useRollbackVersion,
} from '@/hooks/useVersionHistory'
import { useActiveSchedule, useCancelSchedule } from '@/hooks/useActiveSchedule'
import type { ContentBlockVersion } from '@/types/versioning'
import type { BlockType } from '@/types/content-blocks'

interface VersionHistoryOffcanvasProps {
  entityType: 'content_block' | 'channel_package' | 'product_asset_link'
  entityId: string
  blockType?: BlockType  // Required for content_block diff
  channelId?: string     // Required for channel_package
  isOpen: boolean
  onClose: () => void
  onScheduleVersion?: (versionId: string) => void
}

export function VersionHistoryOffcanvas({
  entityType,
  entityId,
  blockType,
  channelId,
  isOpen,
  onClose,
  onScheduleVersion,
}: VersionHistoryOffcanvasProps) {
  const { t } = useTranslation()
  // State
  const [compareMode, setCompareMode] = useState(false)
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null)
  const [showDiff, setShowDiff] = useState(false)

  // Queries
  const versionsQuery = useVersionHistory({
    entityType,
    entityId,
    channelId,
    enabled: isOpen,
  })

  const activeScheduleQuery = useActiveSchedule({
    entityType,
    entityId,
    enabled: isOpen,
  })

  // Mutations
  const rollbackMutation = useRollbackVersion(entityType, entityId, channelId)
  const cancelScheduleMutation = useCancelSchedule(entityType, entityId, channelId)

  // Group versions
  const groupedVersions = useGroupedVersionHistory(
    versionsQuery.data?.versions,
    versionsQuery.data?.current_version_id,
    activeScheduleQuery.data?.schedule
  )

  // Handlers
  const handleCompare = useCallback((versionId: string) => {
    setCompareVersionId(versionId)
    setShowDiff(true)
  }, [])

  const handleRestore = useCallback((versionId: string) => {
    if (confirm('Diese Version wiederherstellen? Dies erstellt eine neue Version.')) {
      rollbackMutation.mutate(versionId)
    }
  }, [rollbackMutation])

  const handleCancelSchedule = useCallback(() => {
    const schedule = activeScheduleQuery.data?.schedule
    if (schedule && confirm('Zeitplan abbrechen?')) {
      cancelScheduleMutation.mutate(schedule.id)
    }
  }, [activeScheduleQuery.data?.schedule, cancelScheduleMutation])

  const handleCloseDiff = useCallback(() => {
    setShowDiff(false)
    setCompareVersionId(null)
  }, [])

  // Current version for diff
  const currentVersion = groupedVersions.current as ContentBlockVersion | null
  const compareVersion = versionsQuery.data?.versions.find(
    v => v.id === compareVersionId
  ) as ContentBlockVersion | undefined

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg overflow-y-auto">
        <SheetHeader className="border-b border-neutral-200 pb-4">
          <SheetTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Versionen
          </SheetTitle>
          <SheetDescription>
            Versionsverlauf und Vergleich
          </SheetDescription>
        </SheetHeader>

        <div className="py-4">
          {/* Diff View */}
          {showDiff && currentVersion && compareVersion && blockType && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-3">
                <h6 className="text-sm font-medium text-neutral-900">
                  Vergleich: V{compareVersion.version_number} → V{currentVersion.version_number}
                </h6>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseDiff}
                  aria-label={t('publishing.closeComparison')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <DiffRouter
                blockType={blockType}
                oldContent={compareVersion.content as ContentBlockContent}
                newContent={currentVersion.content as ContentBlockContent}
                oldLabel={`V${compareVersion.version_number}`}
                newLabel={`V${currentVersion.version_number} (Aktuell)`}
              />
              <div className="mt-3">
                <Button
                  variant="outline"
                  onClick={() => handleRestore(compareVersion.id)}
                  disabled={rollbackMutation.isPending}
                  className="text-amber-600 border-amber-600 hover:bg-amber-50"
                >
                  {rollbackMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-2" />
                  )}
                  {t('publishing.revertTo', { version: compareVersion.version_number })}
                </Button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {versionsQuery.isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
              <span className="sr-only">{t('common.loading')}</span>
            </div>
          )}

          {/* Error State */}
          {versionsQuery.isError && (
            <Alert variant="error">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('errors.loading')}
              </AlertDescription>
            </Alert>
          )}

          {/* Version List */}
          {!versionsQuery.isLoading && !showDiff && (
            <div className="space-y-4">
              {/* Current Version */}
              {groupedVersions.current && (
                <div>
                  <VersionSection
                    title="Aktuell"
                    icon={<CheckCircle className="h-4 w-4 text-primary-500" />}
                    variant="primary"
                  />
                  <VersionCard
                    version={groupedVersions.current as ContentBlockVersion}
                    variant="current"
                    onCompare={() => setCompareMode(!compareMode)}
                  />
                </div>
              )}

              {/* Active/Scheduled Version */}
              {groupedVersions.scheduled && (
                <div>
                  <VersionSection
                    title="Geplant"
                    icon={<Clock className="h-4 w-4 text-amber-500" />}
                    variant="warning"
                  />
                  <VersionCard
                    version={groupedVersions.scheduled as ContentBlockVersion}
                    variant="scheduled"
                    schedule={activeScheduleQuery.data?.schedule}
                    onCancelSchedule={handleCancelSchedule}
                  />
                </div>
              )}

              {/* History */}
              {groupedVersions.history.length > 0 && (
                <div>
                  <VersionSection
                    title="Verlauf"
                    icon={<History className="h-4 w-4 text-neutral-500" />}
                    count={groupedVersions.history.length}
                  />
                  <div className="version-timeline space-y-2">
                    {groupedVersions.history.map((version) => (
                      <VersionCard
                        key={version.id}
                        version={version as ContentBlockVersion}
                        variant="history"
                        onCompare={() => handleCompare(version.id)}
                        onRestore={() => handleRestore(version.id)}
                        onSchedule={onScheduleVersion
                          ? () => onScheduleVersion(version.id)
                          : undefined
                        }
                        isCompareMode={compareMode}
                        isSelected={compareVersionId === version.id}
                        onSelect={() => handleCompare(version.id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!groupedVersions.current && groupedVersions.history.length === 0 && (
                <div className="text-center py-8">
                  <History className="h-12 w-12 text-neutral-400 mx-auto mb-3 opacity-50" />
                  <p className="text-neutral-500">
                    Keine Versionen vorhanden
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
